import { Controller } from '@hotwired/stimulus'
import debounce from 'lodash/debounce'
import { watch } from 'vue'

import type { ColumnConfig } from '@app/application/report/column-editor/types'

import { createApp } from '@app/adapters/create-app'
import { SfLog } from '@app/adapters/logger'
import {
  fetchAppliedColumnsRequest,
  fetchColumnsPreviewRequest,
} from '@app/application/report/api/columns'
import ColumnEditorDrawer from '@app/application/report/column-editor/components/column-editor-drawer.vue'
import {
  blankReportColumns,
  findReportTable,
  isPreviewing,
  renderPreviewColumns,
  restoreReportColumns,
} from '@app/application/report/column-editor/report-column-preview'
import { swapReportTable } from '@app/application/report/column-editor/report-table-swap'
import { useColumnEditor } from '@app/application/report/column-editor/use-column-editor'
import getPiniaInstance from '@app/shared/pinia/instance'

// Long enough that dragging a row or clicking through a dropdown settles into a
// single request, short enough that one edit still reads as immediate.
const PREVIEW_DEBOUNCE_MS = 250

// The frame the report table is rendered into (reports/show.html.slim). It loads
// lazily, so the table can arrive after the drawer is already open.
const TABLE_FRAME_ID = 'report-table'

export default class extends Controller {
  static targets = ['drawer']

  private drawerTarget!: HTMLElement

  private app: ReturnType<typeof createApp> | null = null

  private store: ReturnType<typeof useColumnEditor> | null = null

  private stopWatchers: (() => void)[] = []

  // Only the newest preview counts: edits outpace the responses, and an earlier
  // one landing last would leave the table showing a config the drawer has
  // already moved on from.
  private previewRequest = 0

  // Bumped on every open, so a response can tell the drawer it was asked from
  // apart from a later one. Closing alone is not enough to tell them apart: an
  // apply the user backed out of would find the drawer open again and put its
  // columns — the ones from before the cancel — on the report, closing the
  // drawer over the edits made since.
  private openGeneration = 0

  connect(): void {
    document.addEventListener('turbo:frame-load', this.reapplyPreview)

    // Turbolinks caches the page on the way out, and `disconnect` is too late to
    // undo the preview for it: Stimulus only disconnects once the incoming body
    // has replaced the outgoing one, by which point the table the preview is
    // painted over is detached and `findReportTable` looks straight past it. The
    // snapshot would then be cached mid-preview, and going back to it would show
    // the report with its own columns hidden behind preview cells for nothing.
    // This fires while the outgoing page is still the document.
    document.addEventListener('turbolinks:before-cache', this.teardownPreview)
  }

  // Triggered by the subnav button, which carries the report id and the period
  // set currently on screen as action params
  // (`data-reports--column-editor-report-id-param` and
  // `-report-period-set-id-param`).
  open(
    event: Event & {
      params: { reportId: number; reportPeriodSetId?: number }
    }
  ): void {
    const store = this.mountDrawer()

    // The subnav button stays clickable behind the open drawer, and clicking it
    // again means nothing: the store is already configured and open, so all a
    // second pass would do is blank the table and rebuild every preview cell to
    // arrive back where it started.
    if (store.isOpen) return

    this.openGeneration += 1

    // A report is always viewed through some period set, but only an explicitly
    // selected one is rendered as a param.
    store.configure({
      reportId: event.params.reportId,
      reportPeriodSetId: event.params.reportPeriodSetId ?? null,
    })
    store.open()

    // The report keeps its own columns until there is a preview to put in their
    // place — hiding them up front empties the table for as long as the first
    // request takes, which reads as the report having been wiped. They do go
    // quiet straight away, so the preview arriving only moves columns about
    // instead of also blanking every figure at that moment.
    blankReportColumns(findReportTable())

    // Scheduled rather than fired: loading the columns changes them, which the
    // watcher below also schedules a preview for, and one request is enough.
    // Re-opening a drawer whose columns are already loaded changes nothing, so
    // this is the only thing asking for a preview on that path.
    void store.load().then(this.schedulePreview)
  }

  disconnect(): void {
    document.removeEventListener('turbo:frame-load', this.reapplyPreview)
    document.removeEventListener(
      'turbolinks:before-cache',
      this.teardownPreview
    )

    this.stopWatchers.forEach((stop) => stop())
    this.stopWatchers = []

    this.teardownPreview()

    this.app?.unmount()
    this.app = null
    this.store = null
  }

  private mountDrawer(): ReturnType<typeof useColumnEditor> {
    if (this.store) return this.store

    this.app = createApp(ColumnEditorDrawer)
    this.app.mount(this.drawerTarget)

    // Resolved here, not in a field initializer: the Pinia instance is a page
    // singleton that gets reset on navigation, so the store must come from the
    // same instance the drawer app was just mounted with.
    const store = useColumnEditor(getPiniaInstance())
    this.store = store

    this.stopWatchers.push(
      // Any change to the list — a value edited, a column added or removed, the
      // order dragged about — is a different set of columns to preview.
      watch(() => store.columns, this.schedulePreview, { deep: true }),
      watch(
        () => store.isOpen,
        (isOpen) => {
          if (!isOpen) this.teardownPreview()
        }
      ),
      // The drawer asks; replacing the report table is this controller's job.
      watch(
        () => store.applyRequests,
        () => void this.applyColumns()
      )
    )

    return store
  }

  private schedulePreview = debounce(
    () => void this.refreshPreview(),
    PREVIEW_DEBOUNCE_MS
  )

  private async refreshPreview(): Promise<void> {
    const store = this.store
    if (!store?.isOpen || !store.reportId) return

    const table = findReportTable()
    if (!table) return

    // A load that failed is a preview that is never coming, and the report has
    // been sitting there saying nothing since the drawer opened (see `open`). So
    // it gets its own columns back and reads as itself again while the drawer
    // offers a retry — which lands here again, with something to show this time.
    if (store.status === 'error') {
      restoreReportColumns(table)
      return
    }

    // Nothing to preview until the drawer knows what the report's columns are.
    // `configure` empties them on the way to loading them, and the watcher below
    // schedules a preview for that — which, if the load is slower than the
    // debounce, would ask what an empty set of columns looks like and answer by
    // collapsing the report to nothing but its row labels. The report stays quiet
    // for that: the columns are on their way.
    if (store.status !== 'loaded') return

    // Nothing previewed means the report's own figures are still on screen, at
    // their own widths — a retried load lands here having just had them given
    // back. They go quiet for the same reason they do on open: so the preview
    // arriving only moves columns about instead of also resizing them.
    if (!isPreviewing(table)) blankReportColumns(table)

    const request = (this.previewRequest += 1)

    try {
      const { columns } = await fetchColumnsPreviewRequest({
        reportId: store.reportId,
        columns: this.columnConfigs(),
      })

      if (this.previewIsStale(request, table)) return

      renderPreviewColumns(table, columns)
      store.previewSucceeded()
    } catch (error) {
      // Whatever was previewed stays on screen: it is one edit out of date,
      // which beats dropping back to real columns the drawer isn't editing. With
      // nothing previewed yet there is nothing to keep, and the report has been
      // sitting there saying nothing — so it gets its own columns back.
      //
      // Superseded requests keep their hands off the table, and say nothing about
      // what the drawer is showing either: a newer preview is on its way, will
      // report for itself, and giving the real columns back for it to hide again
      // is the reflow the report was blanked to avoid.
      if (!this.previewIsStale(request, table)) {
        if (!isPreviewing(table)) restoreReportColumns(table)

        // Otherwise the drawer's controls read as doing nothing at all — or
        // worse, the table keeps a preview that no longer matches the list.
        store.previewErrored()
      }

      SfLog.error('Failed to preview the report columns', {
        error,
        extra: { reportId: store.reportId },
      })
    }
  }

  // A preview is worth putting on screen only if it is still the newest one, the
  // drawer is still open, and the table it was captured against is still the
  // table on the page.
  //
  // That last one is not covered by the counter. `refreshPreview` gives up
  // before bumping it when there is no table to preview into — which is what a
  // frame reload landing on the spinner or the error block leaves behind — so
  // the request in flight against the table that just went away is never
  // superseded, and would otherwise render into a detached node: no preview on
  // screen, a table nobody can reach marked as carrying one, and tooltips hung
  // off the same unreachable DOM.
  private previewIsStale(request: number, table: HTMLTableElement): boolean {
    return (
      request !== this.previewRequest ||
      !this.store?.isOpen ||
      !table.isConnected
    )
  }

  // Whether the drawer this apply was asked from is still the one on screen —
  // closed, navigated away from, or closed and opened again all mean its answer
  // is no longer wanted.
  private applyIsStale(generation: number): boolean {
    return !this.store?.isOpen || this.openGeneration !== generation
  }

  // What the drawer holds, as the server wants it. Shared by the preview and the
  // apply so the two cannot drift into asking about different columns.
  private columnConfigs(): ColumnConfig[] {
    return (this.store?.columns ?? []).map((column) => ({
      type: column.type,
      values: column.values,
    }))
  }

  // Renders the report for the columns the drawer holds and puts that table on
  // the page, then closes the drawer: what was being previewed is now what the
  // report shows. Nothing is saved — the columns are applied for as long as this
  // table is on screen, and the next load brings the report's own back.
  private async applyColumns(): Promise<void> {
    const store = this.store
    if (!store?.isOpen || !store.reportId) return
    if (store.status !== 'loaded' || store.applying) return

    // The frame can still be loading its table — the same reason there is no
    // preview on screen to apply. Rendering into it would put the applied table
    // where the spinner is, only for the frame's own load to land moments later
    // and replace it with the report's own columns; the drawer would be closed
    // by then, leaving nothing on screen to say where they went. Reported rather
    // than dropped, or the button reads as doing nothing at all.
    if (!findReportTable()) {
      store.applyErrored()
      return
    }

    store.applyStarted()

    const generation = this.openGeneration

    try {
      const html = await fetchAppliedColumnsRequest({
        reportId: store.reportId,
        columns: this.columnConfigs(),
      })

      // The drawer can be closed — or the page navigated away from — while the
      // render is on its way, and both have already given the report its own
      // columns back. Putting this table on the page now would change the
      // report to columns the user backed out of, with nothing on screen left
      // to say where they came from. A drawer that has since been opened again
      // is the same cancelled request, only harder to see: it is open, but not
      // the one that asked for this.
      if (this.applyIsStale(generation)) return

      // Swapped before the drawer closes, so a table that can't be put on the
      // page leaves the drawer open to say so. Closing then tears the preview
      // down against the new table, which disposes the old table's tooltips
      // without finding anything of its own to undo.
      swapReportTable(html)
      store.close()
    } catch (error) {
      // Only while the drawer that asked for it is still there to show it.
      // Closing clears both errors precisely so the next open doesn't greet the
      // user with a complaint about a request from before it — and by then the
      // report has its own columns back, so there is nothing on screen the
      // complaint is about. Same guard the success path takes.
      if (!this.applyIsStale(generation)) store.applyErrored()

      SfLog.error('Failed to apply the report columns', {
        error,
        extra: { reportId: store.reportId },
      })
    } finally {
      store.applyFinished()
    }
  }

  // The table frame loads lazily and reloads on its own (a period set change, a
  // finished async render), each time replacing the table the preview was
  // applied to. The fresh table shows its own columns until the preview lands on
  // it, same as on open.
  private reapplyPreview = (event: Event): void => {
    if ((event.target as HTMLElement).id !== TABLE_FRAME_ID) return
    if (!this.store?.isOpen) return

    blankReportColumns(findReportTable())

    // The fresh table needs a preview now, so a debounced one queued behind an
    // edit made just before the reload is a second request for the same state.
    this.schedulePreview.cancel()

    void this.refreshPreview()
  }

  // Bound, because it is a document listener as well as a method.
  private teardownPreview = (): void => {
    this.schedulePreview.cancel()
    restoreReportColumns(findReportTable())
  }
}
